/**
 * cellularPottsPipeline — Cellular Potts Model (CPM).
 *
 * The CPM is a Monte Carlo simulation of cells on a grid. Each cell
 * has an ID and a target volume. The Metropolis algorithm tries to
 * copy a pixel's identity to a neighbor; the copy is accepted with
 * probability exp(-dH/T) where dH is the energy change.
 *
 * Result: cells sort themselves, grow, divide, and migrate.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CellularPottsPipeline {
  updateProgram: WebGLProgram | null;
  renderProgram: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  stateTextures: [WebGLTexture | null, WebGLTexture | null];
  stateBuffers: [WebGLFramebuffer | null, WebGLFramebuffer | null];
  pingIdx: number;
  bands: Float32Array;
  gridSize: number;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// State: R = cell id (0-15), G = target volume high byte, B = target volume low byte.
// For simplicity we use 16 cell types and assume fixed volumes.
const UPDATE_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outState;

uniform sampler2D uPrevState;
uniform float uTime;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uPrevState, 0));
  vec4 me = texture(uPrevState, vUV);
  // Randomly pick a neighbor direction.
  float h = hash21(vUV * 100.0 + uTime);
  vec2 dir;
  if (h < 0.25) dir = vec2(texel.x, 0.0);
  else if (h < 0.5) dir = vec2(-texel.x, 0.0);
  else if (h < 0.75) dir = vec2(0.0, texel.y);
  else dir = vec2(0.0, -texel.y);
  vec2 neighborUv = mod(vUV + dir, vec2(1.0));
  vec4 nb = texture(uPrevState, neighborUv);
  // If same cell, accept automatically.
  if (abs(me.r - nb.r) < 0.01) {
    outState = me;
    return;
  }
  // Compute the energy change.
  // For each neighbor of the source pixel (excluding target), check if
  // it has the same id as me.
  float oldContacts = 0.0;
  float newContacts = 0.0;
  // Check 8 neighbors of vUV.
  for (int dy = -1; dy <= 1; dy++) {
    for (int dx = -1; dx <= 1; dx++) {
      if (dx == 0 && dy == 0) continue;
      vec2 nbUv = mod(vUV + vec2(float(dx), float(dy)) * texel, vec2(1.0));
      vec4 n = texture(uPrevState, nbUv);
      if (abs(n.r - me.r) < 0.01) oldContacts += 1.0;
      if (abs(n.r - nb.r) < 0.01) newContacts += 1.0;
    }
  }
  // Energy: negative contact bonus + cell volume penalty.
  // Volume: count neighbors with same id (approximate).
  // For simplicity, use a contact-energy heuristic.
  float J = 1.0;
  float dH = J * (newContacts - oldContacts);
  // Metropolis acceptance.
  float temperature = 5.0;
  float acceptProb = exp(-dH / temperature);
  float accept = step(h, acceptProb);
  outState = mix(me, nb, accept);
}
`;

const RENDER_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uState;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uState, 0));
  float cellId = texture(uState, vUV).r;
  // Determine cell type from id (0-15).
  // Color cells with different palette.
  vec3 cellColor = vec3(
    0.5 + 0.5 * sin(cellId * 1.7 + 0.5),
    0.5 + 0.5 * sin(cellId * 1.7 + 2.6),
    0.5 + 0.5 * sin(cellId * 1.7 + 4.7)
  );
  // Sample neighbors to detect cell boundaries.
  float sameNeighbor = 0.0;
  for (int i = 0; i < 8; i++) {
    float angle = float(i) / 8.0 * 6.2831853;
    vec2 offset = vec2(cos(angle), sin(angle)) * texel;
    sameNeighbor += step(0.5, 1.0 - abs(texture(uState, vUV + offset).r - cellId));
  }
  sameNeighbor /= 8.0;
  // Background: dark gradient.
  vec2 p = vUV * 2.0 - 1.0;
  p.x *= uResolution.x / uResolution.y;
  vec3 bg = vec3(0.03, 0.02, 0.06);
  vec3 col = mix(bg, cellColor, smoothstep(0.5, 0.9, sameNeighbor));
  // Bright on cell edges (where neighbors differ).
  float boundary = 1.0 - sameNeighbor;
  col += vec3(0.95, 0.85, 0.40) * boundary * 0.6;
  // Audio modulation.
  float bassAvg = (uBass.x + uBass.y + uBass.z) / 3.0;
  float midAvg = (uMid.x + uMid.y + uMid.z) / 3.0;
  col *= 1.0 + bassAvg * 0.5;
  col += vec3(0.40, 0.95, 0.85) * midAvg * 0.2;
  outColor = vec4(col, 1.0);
}
`;

function createStateTexture(gl: WebGL2RenderingContext, size: number): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("Failed to create texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  const pixels = new Uint8Array(size * size * 4);
  // Initialize with Voronoi cells: each pixel gets the id of its
  // nearest seed.
  const numSeeds = 20;
  const seeds: number[] = [];
  for (let i = 0; i < numSeeds; i++) {
    seeds.push(Math.floor(Math.random() * size * size));
  }
  for (let i = 0; i < size * size; i++) {
    const x = i % size;
    const y = Math.floor(i / size);
    let minDist = 1e9;
    let closestSeed = 0;
    for (let s = 0; s < numSeeds; s++) {
      const sx = seeds[s] % size;
      const sy = Math.floor(seeds[s] / size);
      const dx = x - sx;
      const dy = y - sy;
      const d = dx * dx + dy * dy;
      if (d < minDist) {
        minDist = d;
        closestSeed = s;
      }
    }
    pixels[i * 4 + 0] = closestSeed * 12;  // cell id (0-255 range)
    pixels[i * 4 + 1] = 0;
    pixels[i * 4 + 2] = 0;
    pixels[i * 4 + 3] = 255;
  }
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

function attachFbo(gl: WebGL2RenderingContext, tex: WebGLTexture, size: number): WebGLFramebuffer {
  const fb = gl.createFramebuffer();
  if (!fb) throw new Error("Failed to create framebuffer");
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.viewport(0, 0, size, size);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return fb;
}

export function createCellularPottsPipeline(gl: WebGL2RenderingContext, size = 96): CellularPottsPipeline | null {
  const updateProgram = linkProgram(gl, VERT_SRC, UPDATE_FRAG_SRC, POST_ATTRIBS);
  const renderProgram = linkProgram(gl, VERT_SRC, RENDER_FRAG_SRC, POST_ATTRIBS);
  if (!updateProgram || !renderProgram) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  const tex0 = createStateTexture(gl, size);
  const tex1 = createStateTexture(gl, size);
  const fb0 = attachFbo(gl, tex0, size);
  const fb1 = attachFbo(gl, tex1, size);

  return {
    updateProgram, renderProgram, vao, vbo,
    stateTextures: [tex0, tex1],
    stateBuffers: [fb0, fb1],
    pingIdx: 0,
    bands: new Float32Array(9),
    gridSize: size,
  };
}

export function computeCellularPottsBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeCellularPottsRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function drawCellularPotts(
  gl: WebGL2RenderingContext,
  pipe: CellularPottsPipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.updateProgram || !pipe.renderProgram) return;
  const size = pipe.gridSize;
  // Run several update steps per frame.
  for (let i = 0; i < 8; i++) {
    const srcIdx = pipe.pingIdx;
    const dstIdx = 1 - pipe.pingIdx;
    const srcTex = pipe.stateTextures[srcIdx];
    const dstFb = pipe.stateBuffers[dstIdx];
    if (!srcTex || !dstFb) continue;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dstFb);
    gl.viewport(0, 0, size, size);
    gl.useProgram(pipe.updateProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    const uPrev = gl.getUniformLocation(pipe.updateProgram, "uPrevState");
    if (uPrev) gl.uniform1i(uPrev, 0);
    const uTime = gl.getUniformLocation(pipe.updateProgram, "uTime");
    if (uTime) gl.uniform1f(uTime, t + Number(i) * 0.1);
    gl.bindVertexArray(pipe.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindVertexArray(null);
    pipe.pingIdx = dstIdx;
  }
  const finalTex = pipe.stateTextures[pipe.pingIdx];
  if (!finalTex) return;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(pipe.renderProgram);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, finalTex);
  const uState = gl.getUniformLocation(pipe.renderProgram, "uState");
  if (uState) gl.uniform1i(uState, 0);
  const uResolution = gl.getUniformLocation(pipe.renderProgram, "uResolution");
  if (uResolution) gl.uniform2f(uResolution, width, height);
  const uTime = gl.getUniformLocation(pipe.renderProgram, "uTime");
  if (uTime) gl.uniform1f(uTime, t);
  const uBass = gl.getUniformLocation(pipe.renderProgram, "uBass");
  if (uBass) gl.uniform3f(uBass, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  const uMid = gl.getUniformLocation(pipe.renderProgram, "uMid");
  if (uMid) gl.uniform3f(uMid, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  const uHigh = gl.getUniformLocation(pipe.renderProgram, "uHigh");
  if (uHigh) gl.uniform3f(uHigh, pipe.bands[6], pipe.bands[7], pipe.bands[8]);

    gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
